import type {
  FlowTraceClickHeatmap,
  FlowTracePublicDisclosure,
  FlowTraceSessionDetail,
  FlowTraceSessionSummary,
} from "../../packages/contracts/src/index";

export const MCP_ENDPOINT = "https://trace-ingest.nex-a.net/api/mcp";

export type McpTokenExpiryDays = 7 | 30 | 90;

export interface FlowTraceProjectItem {
  access?: {
    role: "admin" | "viewer";
    source: "bootstrap" | "local" | "membership";
  };
  appId: string;
  memberCount?: number;
  memberPreview?: Array<{
    image: string | null;
    initial: string;
    name: string | null;
    status: "active" | "invited";
  }>;
  name: string;
  status: "active" | "disabled" | "pending_origin";
  tenantId?: string;
}

export interface FlowTraceProjectMemberItem {
  createdAt: string | null;
  email: string;
  id: string;
  image: string | null;
  name: string | null;
  role: "admin" | "viewer";
  source: "bootstrap" | "membership";
  status: "active" | "invited";
  updatedAt: string | null;
}

export interface McpTokenItem {
  appIds: string[];
  createdAt: string;
  expiresAt: string | null;
  id: string;
  lastUsedAt: string | null;
  name: string;
  revokedAt: string | null;
  tokenPrefix: string;
  tokenSuffix: string;
  updatedAt: string;
}

export interface CreateMcpTokenInput {
  appIds: string[];
  expiresInDays: McpTokenExpiryDays;
  name: string;
}

export interface CreatedMcpToken {
  item: McpTokenItem;
  token: string;
}

const readJson = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    throw new Error(`FlowTrace API request failed: ${response.status}`);
  }
  return (await response.json()) as T;
};

export const listSessions = async (
  appId?: string,
  limit = 100,
): Promise<FlowTraceSessionSummary[]> => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (appId) params.set("appId", appId);
  const query = `?${params.toString()}`;
  const response = await fetch(`/api/sessions${query}`);
  const body = await readJson<{ sessions: FlowTraceSessionSummary[] }>(response);
  return body.sessions;
};

const clickHeatmapCache = new Map<
  string,
  { expiresAt: number; value: Promise<FlowTraceClickHeatmap> }
>();

export const getClickHeatmap = async (input: {
  appId: string;
  days?: number;
  gridSize?: number;
  route: string;
}): Promise<FlowTraceClickHeatmap> => {
  const params = new URLSearchParams({
    appId: input.appId,
    days: String(input.days ?? 30),
    gridSize: String(input.gridSize ?? 24),
    route: input.route,
  });
  const key = params.toString();
  const cached = clickHeatmapCache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.value;
  const value = fetch(`/api/heatmaps/clicks?${key}`, {
    credentials: "include",
  })
    .then((response) =>
      readJson<{ heatmap: FlowTraceClickHeatmap }>(response),
    )
    .then((body) => body.heatmap);
  clickHeatmapCache.set(key, { expiresAt: Date.now() + 30_000, value });
  value.catch(() => {
    if (clickHeatmapCache.get(key)?.value === value) {
      clickHeatmapCache.delete(key);
    }
  });
  return value;
};

export const listProjects = async (): Promise<FlowTraceProjectItem[]> => {
  const response = await fetch("/api/projects", { credentials: "include" });
  const body = await readJson<{ projects: FlowTraceProjectItem[] }>(response);
  return body.projects;
};

export const listProjectMembers = async (
  appId: string,
): Promise<FlowTraceProjectMemberItem[]> => {
  const response = await fetch(
    `/api/projects/${encodeURIComponent(appId)}/members`,
    { credentials: "include" },
  );
  const body = await readJson<{ members: FlowTraceProjectMemberItem[] }>(
    response,
  );
  return body.members;
};

export const addProjectMember = async (
  appId: string,
  input: { email: string; role: "admin" | "viewer" },
): Promise<FlowTraceProjectMemberItem> => {
  const response = await fetch(
    `/api/projects/${encodeURIComponent(appId)}/members`,
    {
      body: JSON.stringify(input),
      credentials: "include",
      headers: { "content-type": "application/json" },
      method: "POST",
    },
  );
  const body = await readJson<{ member: FlowTraceProjectMemberItem }>(response);
  return body.member;
};

export const updateProjectMember = async (
  appId: string,
  membershipId: string,
  role: "admin" | "viewer",
): Promise<FlowTraceProjectMemberItem> => {
  const response = await fetch(
    `/api/projects/${encodeURIComponent(appId)}/members/${encodeURIComponent(membershipId)}`,
    {
      body: JSON.stringify({ role }),
      credentials: "include",
      headers: { "content-type": "application/json" },
      method: "PATCH",
    },
  );
  const body = await readJson<{ member: FlowTraceProjectMemberItem }>(response);
  return body.member;
};

export type FlowTraceMcpErrorDetail = "full" | "redacted";

export const getProjectSettings = async (
  appId: string,
): Promise<{ mcpErrorDetail: FlowTraceMcpErrorDetail }> => {
  const response = await fetch(
    `/api/projects/${encodeURIComponent(appId)}/settings`,
    { credentials: "include" },
  );
  const body = await readJson<{ settings: { mcpErrorDetail: FlowTraceMcpErrorDetail } }>(response);
  return body.settings;
};

export const updateProjectSettings = async (
  appId: string,
  settings: { mcpErrorDetail: FlowTraceMcpErrorDetail },
): Promise<{ mcpErrorDetail: FlowTraceMcpErrorDetail }> => {
  const response = await fetch(
    `/api/projects/${encodeURIComponent(appId)}/settings`,
    {
      body: JSON.stringify(settings),
      credentials: "include",
      headers: { "content-type": "application/json" },
      method: "PATCH",
    },
  );
  const body = await readJson<{ settings: { mcpErrorDetail: FlowTraceMcpErrorDetail } }>(response);
  return body.settings;
};

export const removeProjectMember = async (
  appId: string,
  membershipId: string,
): Promise<void> => {
  const response = await fetch(
    `/api/projects/${encodeURIComponent(appId)}/members/${encodeURIComponent(membershipId)}`,
    { credentials: "include", method: "DELETE" },
  );
  if (!response.ok) {
    throw new Error(`FlowTrace API request failed: ${response.status}`);
  }
};

export const getPublicDisclosure = async (
  appId: string,
): Promise<FlowTracePublicDisclosure> => {
  const response = await fetch(
    `/api/disclosures/${encodeURIComponent(appId)}`,
  );
  const body = await readJson<{ disclosure: FlowTracePublicDisclosure }>(response);
  return body.disclosure;
};

export const getSession = async (
  appId: string,
  sessionId: string,
): Promise<FlowTraceSessionDetail> => {
  const response = await fetch(
    `/api/sessions/${encodeURIComponent(appId)}/${encodeURIComponent(sessionId)}`,
  );
  return readJson<FlowTraceSessionDetail>(response);
};

export const listMcpTokens = async (): Promise<McpTokenItem[]> => {
  const response = await fetch("/api/me/mcp-tokens", {
    credentials: "include",
  });
  const body = await readJson<{ tokens: McpTokenItem[] }>(response);
  return body.tokens;
};

export const createMcpToken = async (
  input: CreateMcpTokenInput,
): Promise<CreatedMcpToken> => {
  const response = await fetch("/api/me/mcp-tokens", {
    body: JSON.stringify(input),
    credentials: "include",
    headers: { "content-type": "application/json" },
    method: "POST",
  });
  return readJson<CreatedMcpToken>(response);
};

export const revokeMcpToken = async (id: string): Promise<void> => {
  const response = await fetch(
    `/api/me/mcp-tokens/${encodeURIComponent(id)}`,
    { credentials: "include", method: "DELETE" },
  );
  if (!response.ok) {
    throw new Error(`FlowTrace API request failed: ${response.status}`);
  }
};
